import { ArfDictionaryItemDto } from "../dictionaries/dictionaries.dto";
import { FileDto } from "../files/files.dto";
import { SectorDto } from "../sectors/sectors.dto";
import { StandardDto } from "../standards/standards.dto";

export interface CompanyInfoDto {
  companyName: string;
  companyPhoneCode: string;
  companyPhone: string;
  taxOffice?: string;
  taxNumber?: string;
  country: string;
  postCode: string;
  districtState: string;
  city: string;
  address: string;
}
export interface PersonInfoDto {
  fullName: string;
  phoneNumber: string;
  email: string;
  taskRole: string;
}

export interface AuthorizedPersonInfoDto {
  fullName: string;
  email: string;
  taskRole: string;
  language: string;
}

export interface ApplicationStep2Dto {
  typeOfProcessId: number;
  otherType?: string;
  haveSocialCertification?: boolean;
  socialCertification?: string;
  socialCertificationValidDate?: Date;
  childLabour?: boolean;
  certifiedInputStandards?: string;
  reasonChildLabour?: string;
  numberOfChildLabour?: number;
}

export interface ApplicationFormsCreateDto {
  company: CompanyDto;
  contactPersonName: string;
  contactPersonTitle: string;
  contactPersonPhoneNumber: string;
  contactPersonPhoneCode: string;
  contactPersonEmail: string;
  certifiedBefore?: boolean;
  certifiedStandards: string;
  hasInspectionReport?: boolean;
  wasCertificationDenied?: boolean;
  certificationDeniedReason: string;
  sectorId: number;
  standardIds: number[];
  twoFactorCode: string;
  connectionCode: string;
  certificationBodyName?: string;
  lastInspectionDate?: Date | null;
  certificateExpirationDate?: Date | null;
  reachSource?: ReachSource;
  reachSourceOtherDetail?: string;
}

export enum ReachSource {
  SocialMedia = 1,
  OtherETKOCertifiedCompany,
  EtkoEmployee,
  ConferenceOrEvent,
  Fair,
  Other,
}

export interface NewApplicationFormDto {
  contactPersonName: string;
  contactPersonTitle: string;
  contactPersonPhoneNumber: string;
  contactPersonPhoneCode: string;
  contactPersonEmail: string;
  certifiedBefore?: boolean;
  certifiedStandards: string;
  hasInspectionReport?: boolean;
  wasCertificationDenied?: boolean;
  certificationDeniedReason: string;
  sectorId: number;
  standardIds: number[];
  certificationBodyName?: string;
  lastInspectionDate?: Date | null;
  certificateExpirationDate?: Date | null;
}

export interface UpdateApplicationFormDto {
  id: number;
  companyId: number;
  company: CompanyDto;
  contactPersonName: string;
  contactPersonTitle: string;
  contactPersonPhoneNumber: string;
  contactPersonPhoneCode: string;
  contactPersonEmail: string;
  certifiedBefore?: boolean;
  certifiedStandards: string;
  hasInspectionReport?: boolean;
  wasCertificationDenied?: boolean;
  certificationDeniedReason: string;
  sectorId: number;
  standardIds: number[];
  certificationBodyName?: string;
  lastInspectionDate?: Date | null;
}

export interface ApplicationFormDto {
  uid: string;
  id: number;
  status: ApplicationStatus;
  companyId: number;
  company: CompanyDto;
  contactPersonName: string;
  contactPersonTitle: string;
  contactPersonPhoneNumber: string;
  contactPersonPhoneCode: string;
  contactPersonEmail: string;
  certifiedBefore?: boolean;
  certifiedStandards: string;
  hasInspectionReport?: boolean;
  wasCertificationDenied?: boolean;
  certificationDeniedReason: string;
  sectorId: number;
  sector: SectorDto;
  applicationStandards: ApplicationStandardDto[];
  standards: BaseStandardDto[];
  connectionCode: string;
  certificationBodyName?: string;
  lastInspectionDate?: Date | null;
  rejectionExplanation?: string;
  contractFileId?: string;
  applicationProformas?: ApplicationProformas[];
  offerAmount?: number;
  creationTime?: Date;
  applicationApproveDate?: Date;
  contractFile?: FileDto;
  certificateExpirationDate?: Date | null;
  applicationContracts?: ApplicationContractDto[];
  receiptFileId?: string;
  paymentReceivedDate?: Date;
  invoiceFileId?: string;
  offerFiles?: ApplicationFormOfferFiles[];
  reachSource?: ReachSource;
  reachSourceOtherDetail?: string;
  rejectApprovedOfferExplanation?: string;
  customerNumber?: string;
  rejectSignedContractExplanation?: string;
  leadAuditorId?: string;
}

export interface ApplicationFormOfferFiles {
  fileId: string;
  file: FileDto;
  applicationFormId: number;
  applicationForm: ApplicationFormDto;
}

export interface SummaryApplicationFormDto {
  uid: string;
  creationTime: Date;
  status: ApplicationStatus;
  companyId: number;
  company: CompanyDto;
  sectorId: number;
  sector: SectorDto;
  applicationStandards: ApplicationStandardDto[];
  id: number;
}

export interface ApplicationContractDto {
  applicationId: number;
  fileId: string;
  file: FileDto;
  standardId: number;
  standard: StandardDto;
  standardContractType: StandardContractType;
}

export enum StandardContractType {
  General = 0,
  AgricultureGeneral = 1,
  GMOFree = 2,
  ITU = 3,
  GlobalGAP = 4,
  GreenChemistryGeneral = 5,
  COSMOS = 6,
  GOTSInput = 7,
  Vegan = 8,
}

export interface ApplicationProformas {
  fileId: string;
}

export interface SendToApproveDto {
  applicationId: number;
}

export interface SaveApplicationEvaluationDto {
  applicationId: number;
}

export interface SendToOfferDto {
  applicationId: number;
}

export interface SendToContractDto {
  applicationId: number;
}
export interface RejectToOfferFormDto {
  rejectionExplanation: string;
}
export interface RejectToOfferDto {
  applicationId?: number;
  rejectionExplanation: string;
}
export interface RejectToOfferByUIDDto {
  uid: string;
  rejectionExplanation: string;
}

export interface SendToOfferDto {
  applicationId: number;
  sentAgain: boolean;
}

export interface SendToContractDto {
  applicationId: number;
  sentAgain: boolean;
}

export interface CreateToContractDto {
  applicationId: number;
  customerNumber: string;
  offerAmount: number;
  currency: string;
  contractNumber?: string;
}

export interface ApproveToOfferDto {
  applicationUID: string;
  offerFileIds: string[];
}

export interface UploadOfferDto {
  applicationId: number;
  offerFileIds: string[];
}

export interface ApproveToContractDto {
  applicationId: number;
  contractFileId: string;
  standardContractType: StandardContractType;
}

export interface UpdateSignedContractDto {
  applicationUID: string;
  contractFileId: string;
  standardContractType: StandardContractType;
}

export interface PaymentReceivedDto {
  applicationId: number;
  invoiceFileId?: string;
}

export interface RejectSignedOfferDto {
  applicationId: number;
  rejectApprovedOfferExplanation: string;
}

export interface RejectSignedContractDto {
  applicationId: number;
  rejectSignedContractExplanation: string;
}

export interface SignedToContractDto {
  applicationUID: string;
  contractFileId: string;
  standardContractType: StandardContractType;
}
export interface PaymentCompletedDto {
  applicationId: number;
  receiptFileId: string;
}
export interface SendToAprStageDto {
  applicationID: number;
  aprOfficerId: string;
}

export interface CompleteAPREvaluationDto {
  applicationId: number;
  isNegative: boolean;
}

export interface GetApplicationHistoryDto {
  from: ApplicationStatus;
  to: ApplicationStatus;
  timestamp: Date;
}

export interface UpdateTROrganicAppFileDto {
  id: number;
  applicationFormFileId: string;
}

export interface BaseStandardDto {
  id: number;
  applicationId: number;
  standardId: number;
  standard?: StandardDto;
  application?: ApplicationFormDto;
  status?: BaseStandardStatus;
  files?: BaseStandardDocumentDto[];
  processTypes?: ProcessTypeOfGeneralStandardDto[];
}

export interface ApplicationStandardDto {
  id: number;
  applicationId: number;
  standardId: number;
  standard: StandardDto;
}

export enum ApplicationStatus {
  PreApplication = 1,
  InProgress = 2,
  NotApprovedApplication = 3,
  ApprovedApplication = 4,
  SentForRevision = 5,
  RevisionCompleted = 6,
  OfferStage = 7,
  OfferSent = 8,
  OfferApproved = 9,
  ContractSent = 10,
  ContractSigned = 11,
  PaymentPending = 12,
  PaymentNotMade = 13,
  RiskEvaluation = 14,
  APRStage = 15,
  OfferRejected = 16,
  OfferReview = 17,
  WaitingPaymentConfirm = 18,
  APROfficerAssignment = 19,
  APRReview = 20,
  CreatedContract = 21,
  RejectApprovedOffer = 22,
  RejectSignedContract = 23,
  RiskEvaluationReview = 24,
  AuditorAssignment = 25,
  LeadAuditorReview = 26,
  WaitingForPlanning = 27,
  PlanningAwaitingApproval = 28,
  AuditStage = 29,
  CertificateStage = 30,
  CustomerNonConfirmitiesApproveStage = 31,
  NonConformityClosureStage = 32,
  CertifierReview = 33,
  NewCertifierAssignment = 34,
  ObjectionReviewStage = 35,
  PositiveCertificationDecision = 36,
  NegativeCertificationDecision = 37,
}

export enum BaseStandardStatus {
  InProgress = 0,
  NotApprovedApplication = 1,
  ApprovedApplication = 2,
}

export interface CompanyDto {
  id?: number;
  name: string;
  address: string;
  postCode: string;
  country?: ArfDictionaryItemDto;
  countryId: number;
  provinceId: number;
  province?: ArfDictionaryItemDto;
  district: string;
  phoneNumber: string;
  phoneCode: string;
  taxOffice: string;
  taxNumber: string;
  authorizedPersonName: string;
  authorizedPersonRole: string;
  authorizedPersonEmail: string;
  authorizedPersonLanguageId?: number;
  authorizedPersonLanguage?: ArfDictionaryItemDto;
  contactPersonName?: string;
  contactPersonTitle?: string;
  contactPersonPhoneNumber?: string;
  contactPersonPhoneCode?: string;
  contactPersonEmail?: string;
}

export interface GenericInvoiceDto {
  applicationId: number;
  creationDate: string;
  fileId: string;
  type: number;
  file: FileDto;
  receiptFileId?: string;
  receiptFile?: FileDto;
}

export interface CosmosStandardDto extends BaseStandardDto {
  applicationId: number;
  standardId: number;
  activityTypes?: string[];
  applicationScopes?: string[];
  hasRegulations?: boolean;
  areProductsChecked?: boolean;
  hasAnyViolations?: boolean;
  didTakeCorrectiveMeasures?: boolean;
  guaranteedProducts?: boolean;
  didSetupForCosmos?: boolean;
}

export interface CosmosStandardCreateDto {
  applicationId: number;
  standardId: number;
  activityTypes?: string[];
  applicationScopes?: string[];
  hasRegulations?: boolean;
  areProductsChecked?: boolean;
  hasAnyViolations?: boolean;
  didTakeCorrectiveMeasures?: boolean;
  guaranteedProducts?: boolean;
  didSetupForCosmos?: boolean;
}

export interface CosmosStandardUpdateDto {
  id: number;
  activityTypes?: string[];
  applicationScopes?: string[];
  hasRegulations?: boolean;
  areProductsChecked?: boolean;
  hasAnyViolations?: boolean;
  didTakeCorrectiveMeasures?: boolean;
  guaranteedProducts?: boolean;
  didSetupForCosmos?: boolean;
  fileIds?: string[];
}

export interface VeganStandardDto extends BaseStandardDto {
  id: number;
  applicationId: number;
  standardId: number;
  activityTypes?: string[];
  areProductsChecked?: boolean;
  confirmNationalRegulations?: boolean;
  hasProductionPlan?: boolean;
  areIngredientsApproved?: boolean;
}

export interface VeganStandardCreateDto {
  applicationId: number;
  standardId: number;
  activityTypes?: string[];
  areProductsChecked?: boolean;
  confirmNationalRegulations?: boolean;
  hasProductionPlan?: boolean;
  areIngredientsApproved?: boolean;
}

export interface VeganStandardUpdateDto {
  id: number;
  activityTypes?: string[];
  areProductsChecked?: boolean;
  confirmNationalRegulations?: boolean;
  hasProductionPlan?: boolean;
  areIngredientsApproved?: boolean;
  fileIds?: string[];
}

export interface DetergentStandardDto extends BaseStandardDto {
  id: number;
  applicationId: number;
  standardId: number;
  activityTypes?: string[];
  hasRegulations?: boolean;
  areProductsChecked?: boolean;
  hasAnyViolations?: boolean;
  didTakeCorrectiveMeasures?: boolean;
  guaranteedProducts?: boolean;
}

export interface DetergentStandardCreateDto {
  applicationId: number;
  standardId: number;
  activityTypes?: string[];
  hasRegulations?: boolean;
  areProductsChecked?: boolean;
  hasAnyViolations?: boolean;
  didTakeCorrectiveMeasures?: boolean;
  guaranteedProducts?: boolean;
}

export interface DetergentStandardUpdateDto {
  id: number;
  activityTypes?: string[];
  hasRegulations?: boolean;
  areProductsChecked?: boolean;
  hasAnyViolations?: boolean;
  didTakeCorrectiveMeasures?: boolean;
  guaranteedProducts?: boolean;
  fileIds?: string[];
}

export interface AnimalCareStandardDto extends BaseStandardDto {
  id: number;
  activityTypes?: string[];
  hasRegulations?: boolean;
  areProductsChecked?: boolean;
  hasAnyViolations?: boolean;
  didTakeCorrectiveMeasures?: boolean;
  guaranteedProducts?: boolean;
}

export interface AnimalCareStandardCreateDto {
  applicationId: number;
  standardId: number;
  activityTypes?: string[];
  hasRegulations?: boolean;
  areProductsChecked?: boolean;
  hasAnyViolations?: boolean;
  didTakeCorrectiveMeasures?: boolean;
  guaranteedProducts?: boolean;
}

export interface AnimalCareStandardUpdateDto {
  id: number;
  activityTypes?: string[];
  hasRegulations?: boolean;
  areProductsChecked?: boolean;
  hasAnyViolations?: boolean;
  didTakeCorrectiveMeasures?: boolean;
  guaranteedProducts?: boolean;
  fileIds?: string[];
}

export interface GotsInputStandardDto extends BaseStandardDto {
  id: number;
  applicationId: number;
  standardId: number;
  activityTypes?: string[];
  applicationScopes?: string[];
  hasPreviousCertification?: boolean;
  areProductSafetyDataSheets?: boolean;
  advertisingPurposes?: boolean;
  certificationDetails?: string;
  doesManufacturerHaveGOTSInputCertificate?: boolean;
}

export interface GotsInputStandardCreateDto {
  applicationId: number;
  standardId: number;
  activityTypes?: string[];
  applicationScopes?: string[];
  hasPreviousCertification?: boolean;
  areProductSafetyDataSheets?: boolean;
  advertisingPurposes?: boolean;
  certificationDetails?: string;
  doesManufacturerHaveGOTSInputCertificate?: boolean;
}

export interface GotsInputStandardUpdateDto {
  id: number;
  activityTypes?: string[];
  applicationScopes?: string[];
  hasPreviousCertification?: boolean;
  areProductSafetyDataSheets?: boolean;
  advertisingPurposes?: boolean;
  certificationDetails?: string;
  doesManufacturerHaveGOTSInputCertificate?: boolean;
  fileIds?: string[];
}

// Textile Standard
export interface CreateBaseStandardDto {
  applicationId: number;
  standardId: number;
}

export interface GeneralStandardDto extends BaseStandardDto {
  processTypes?: ProcessTypeOfGeneralStandardDto[];
  otherProcessType?: string;
  hasSocialCertification?: boolean;
  socialCertification?: string;
  socialCertificationValidityDate?: Date | null;
  hasChildLabour?: boolean;
  numberOfChildLabour?: number;
  reasonForChildLabour?: string;
  typeOfSocialAuditCertified?: string;
}

export interface GeneralStandardUpsertDto extends BaseStandardDto {
  processTypes?: ProcessTypeOfGeneralStandardDto[];
  processTypeIds?: number[];
  otherProcessType?: string;
  hasSocialCertification?: boolean;
  socialCertification?: string;
  socialCertificationValidityDate?: Date | null;
  hasChildLabour?: boolean;
  numberOfChildLabour?: number;
  reasonForChildLabour?: string;
  typeOfSocialAuditCertified?: string;
}

interface ProcessTypeOfGeneralStandardDto {
  id: number;
  generalStandardId: number;
  processTypeId: number;
  processType: ProcessTypeDto;
}

interface ProcessTypeDto {
  code: string;
  name: string;
  sectorId: number;
  sector: SectorDto;
  standardId: number;
  standard: StandardDto;
}

export interface CreateGeneralStandardDto extends CreateBaseStandardDto {
  processTypeIds?: number[];
  otherProcessType?: string;
  hasSocialCertification?: boolean;
  socialCertification?: string;
  socialCertificationValidityDate?: Date | null;
  hasChildLabour?: boolean;
  numberOfChildLabour?: number;
  reasonForChildLabour?: string;
  typeOfSocialAuditCertified?: string;
}

export interface UpdateGeneralStandardDto {
  id: number;
  processTypeIds?: number[];
  otherProcessType?: string;
  hasSocialCertification?: boolean;
  socialCertification?: string;
  socialCertificationValidityDate?: Date | null;
  hasChildLabour?: boolean;
  numberOfChildLabour?: number;
  reasonForChildLabour?: string;
  typeOfSocialAuditCertified?: string;
  fileIds?: string[];
}

export interface TrOrganicStandardDto extends BaseStandardDto {
  certificationScopes?: string[];
  areRegisteredMinistrySystem?: boolean;
  individualFarmerOrBreederGroupForProducts?: string;
  sizeProductionAreaToBeCertified?: number;
  numberOfParcels?: number;
  hasConversionProduction?: boolean;
  startDateOfOrganicProduction?: Date | null;
  hasNonorganicProduction?: boolean;
  sizeProductionArea?: number;
  numberOfAreasToBeProduced?: number;
  totalProductionArea?: number;
  numberOfManufacturers?: number;
  averageAreaPerProducer?: number;
  areProducerOver5Ha?: boolean;
  numberOfProducerOver5Ha?: number;
  averageTravelTimeProducers?: number;
  numberOfDifferentRegionsProducers?: number;
  arePurchaseDistributionFromCentral?: boolean;
  areProduceConventionalProduct?: boolean;
  areParallelProduction?: boolean;
  hasInternalControlSystem?: boolean;
  totalTimeOfAnnualInternalAudit?: number;
  hasInternalQualitySystem?: boolean;
  totalAreaOfCollectionAreasToBeCertified?: number;
  annualEstimatedAmountCollected?: number;
  numberOfCollectionAreas?: number;
  numberOfCollectors?: number;
  numberOfOrganizers?: number;
  files?: BaseStandardDocumentDto[];
  individualFarmerOrBreederGroupForBeekeeping?: string;
  numberOfGroupMembersForBeekeeping?: number;
  totalNumberOfBeehives?: number;
  numberOfAreasForBeekeeping?: number;
  numberOfBeehives?: number;
  areBeehivesLocatedMoreOneArea?: boolean;
  numberOfBeehivesDifferentArea?: number;
  estimatedAmountHoneyProduction?: number;
  hasAnyNonOrganicBeehives?: boolean;
  individualFarmerOrBreederGroupForLivestock?: string;
  numberOfGroupMembersForLivestock?: number;
  totalNumberOfAnimals?: number;
  numberOfAreasForLivestock?: number;
  isFeedPreparedInYourBusiness?: string;
  hasAnyNonOrganicProduction?: boolean;
  estimatedAnnualAmountOfTradedProducts?: number;
  areThereARecertificationRequest?: boolean;
  typeOfProductToBeApproved?: string;
  standards?: string[];
  hasARegistrationCertificate?: boolean;
  applicationFormFileId?: string;
  applicationFormFile?: FileDto;
  totalNumberOfInputsOrganic?: number;
  totalNumberOfInputsNonOrganic?: number;
  weightType?: string;
  permissionLetterDocuments?: BaseStandardDocumentDto[];
  nonOrganicProducts?: string;
  isThereStorageNonOrganic?: boolean;
  productsForBeekeeping?: string[];
}

export interface CreateTrOrganicStandardDto extends CreateBaseStandardDto {
  certificationScopes: string[];
  areRegisteredMinistrySystem?: boolean;
}

export interface UpdateTrOrganicStandardDto {
  id: number;
  certificationScopes: string[];
  areRegisteredMinistrySystem?: boolean;
}

export interface UpdateTROrganikPartialDto {
  id: number;
  individualFarmerOrBreederGroupForProducts?: string;
  sizeProductionAreaToBeCertified?: number;
  numberOfParcels?: number;
  hasConversionProduction?: boolean;
  startDateOfOrganicProduction?: Date | null;
  hasNonorganicProduction?: boolean;
  sizeProductionArea?: number;
  numberOfAreasToBeProduced?: number;
  totalProductionArea?: number;
  numberOfManufacturers?: number;
  averageAreaPerProducer?: number;
  areProducerOver5Ha?: boolean;
  numberOfProducerOver5Ha?: number;
  averageTravelTimeProducers?: number;
  numberOfDifferentRegionsProducers?: number;
  arePurchaseDistributionFromCentral?: boolean;
  areProduceConventionalProduct?: boolean;
  areParallelProduction?: boolean;
  hasInternalControlSystem?: boolean;
  totalTimeOfAnnualInternalAudit?: number;
  hasInternalQualitySystem?: boolean;
  totalAreaOfCollectionAreasToBeCertified?: number;
  annualEstimatedAmountCollected?: number;
  numberOfCollectionAreas?: number;
  numberOfCollectors?: number;
  numberOfOrganizers?: number;
  permissionLetterIds?: string[];
  individualFarmerOrBreederGroupForBeekeeping?: string;
  numberOfGroupMembersForBeekeeping?: number;
  totalNumberOfBeehives?: number;
  numberOfAreasForBeekeeping?: number;
  numberOfBeehives?: number;
  areBeehivesLocatedMoreOneArea?: boolean;
  numberOfBeehivesDifferentArea?: number;
  estimatedAmountHoneyProduction?: number;
  hasAnyNonOrganicBeehives?: boolean;
  individualFarmerOrBreederGroupForLivestock?: string;
  numberOfGroupMembersForLivestock?: number;
  totalNumberOfAnimals?: number;
  numberOfAreasForLivestock?: number;
  isFeedPreparedInYourBusiness?: string;
  hasAnyNonOrganicProduction?: boolean;
  estimatedAnnualAmountOfTradedProducts?: number;
  areThereARecertificationRequest?: boolean;
  typeOfProductToBeApproved?: string;
  standards?: string[];
  hasARegistrationCertificate?: boolean;
  applicationFormFileId?: string;
  totalNumberOfInputsOrganic?: number;
  totalNumberOfInputsNonOrganic?: number;
  weightType?: string;
  fileIds?: string[];
  nonOrganicProducts?: string;
  isThereStorageNonOrganic?: boolean;
  productsForBeekeeping?: string[];
}

export interface NOPStandardDto extends BaseStandardDto {
  certificationScopes?: string[];
  doYouProduceFeedProducts?: boolean;
  individualFarmerOrBreederGroupForProducts?: string;
  sizeProductionAreaToBeCertified?: number;
  numberOfParcels?: number;
  hasConversionProduction?: boolean;
  startDateOfOrganicProduction?: Date | null;
  hasNonorganicProduction?: boolean;
  sizeProductionArea?: number;
  numberOfAreasToBeProduced?: number;
  totalProductionArea?: number;
  numberOfManufacturers?: number;
  averageAreaPerProducer?: number;
  areProducerOver5Ha?: boolean;
  numberOfProducerOver5Ha?: number;
  averageTravelTimeProducers?: number;
  numberOfDifferentRegionsProducers?: number;
  arePurchaseDistributionFromCentral?: boolean;
  areProduceConventionalProduct?: boolean;
  areParallelProduction?: boolean;
  hasInternalControlSystem?: boolean;
  totalTimeOfAnnualInternalAudit?: number;
  hasInternalQualitySystem?: boolean;
  totalAreaOfCollectionAreasToBeCertified?: number;
  annualEstimatedAmountCollected?: number;
  numberOfCollectionAreas?: number;
  numberOfCollectors?: number;
  numberOfOrganizers?: number;
  files?: BaseStandardDocumentDto[];
  hasAnyNonOrganicProduction?: boolean;
  estimatedAnnualAmountOfTradedProducts?: number;
  areThereARecertificationRequest?: boolean;
  totalNumberOfInputsOrganic?: number;
  totalNumberOfInputsNonOrganic?: number;
  weightType?: string;
  permissionLetterDocuments?: BaseStandardDocumentDto[];
  nonOrganicProducts?: string;
  isThereStorageNonOrganic?: boolean;
}

export interface CreateNOPStandardDto extends CreateBaseStandardDto {
  certificationScopes: string[];
  doYouProduceFeedProducts?: boolean;
}

export interface UpdateNOPStandardDto {
  id: number;
  certificationScopes: string[];
  doYouProduceFeedProducts?: boolean;
}

export interface UpdateNOPPartialDto {
  id: number;
  individualFarmerOrBreederGroupForProducts?: string;
  sizeProductionAreaToBeCertified?: number;
  numberOfParcels?: number;
  hasConversionProduction?: boolean;
  startDateOfOrganicProduction?: Date | null;
  hasNonorganicProduction?: boolean;
  sizeProductionArea?: number;
  numberOfAreasToBeProduced?: number;
  totalProductionArea?: number;
  numberOfManufacturers?: number;
  averageAreaPerProducer?: number;
  areProducerOver5Ha?: boolean;
  numberOfProducerOver5Ha?: number;
  averageTravelTimeProducers?: number;
  numberOfDifferentRegionsProducers?: number;
  arePurchaseDistributionFromCentral?: boolean;
  areProduceConventionalProduct?: boolean;
  areParallelProduction?: boolean;
  hasInternalControlSystem?: boolean;
  totalTimeOfAnnualInternalAudit?: number;
  hasInternalQualitySystem?: boolean;
  totalAreaOfCollectionAreasToBeCertified?: number;
  annualEstimatedAmountCollected?: number;
  numberOfCollectionAreas?: number;
  numberOfCollectors?: number;
  numberOfOrganizers?: number;
  permissionLetterIds?: string[];
  hasAnyNonOrganicProduction?: boolean;
  estimatedAnnualAmountOfTradedProducts?: number;
  areThereARecertificationRequest?: boolean;
  totalNumberOfInputsOrganic?: number;
  totalNumberOfInputsNonOrganic?: number;
  weightType?: string;
  fileIds?: string[];
  nonOrganicProducts?: string;
  isThereStorageNonOrganic?: boolean;
}

export interface EUStandardDto extends BaseStandardDto {
  certificationScopes?: string[];
  productCategories?: string[];
  doYouProduceFeedProducts?: boolean;
  individualFarmerOrBreederGroupForProducts?: string;
  sizeProductionAreaToBeCertified?: number;
  numberOfParcels?: number;
  hasConversionProduction?: boolean;
  startDateOfOrganicProduction?: Date | null;
  hasNonorganicProduction?: boolean;
  sizeProductionArea?: number;
  numberOfAreasToBeProduced?: number;
  totalProductionArea?: number;
  numberOfManufacturers?: number;
  averageAreaPerProducer?: number;
  aeProducerOver5Ha?: boolean;
  numberOfProducerOver5Ha?: number;
  averageTravelTimeProducers?: number;
  numberOfDifferentRegionsProducers?: number;
  arePurchaseDistributionFromCentral?: boolean;
  areProduceConventionalProduct?: boolean;
  areParallelProduction?: boolean;
  hasInternalControlSystem?: boolean;
  totalTimeOfAnnualInternalAudit?: number;
  hasInternalQualitySystem?: boolean;
  totalAreaOfCollectionAreasToBeCertified?: number;
  annualEstimatedAmountCollected?: number;
  numberOfCollectionAreas?: number;
  numberOfCollectors?: number;
  numberOfOrganizers?: number;
  files?: BaseStandardDocumentDto[];
  hasAnyNonOrganicProduction?: boolean;
  estimatedAnnualAmountOfTradedProducts?: number;
  areThereARecertificationRequest?: boolean;
  totalNumberOfInputsOrganic?: number;
  totalNumberOfInputsNonOrganic?: number;
  weightType?: string;
  permissionLetterDocuments?: BaseStandardDocumentDto[];
  nonOrganicProducts?: string;
  isThereStorageNonOrganic?: boolean;
}

export interface CreateEUStandardDto extends CreateBaseStandardDto {
  certificationScopes: string[];
  productCategories?: string[];
  doYouProduceFeedProducts?: boolean;
}

export interface UpdateEUStandardDto {
  id: number;
  certificationScopes: string[];
  productCategories?: string[];
  doYouProduceFeedProducts?: boolean;
}

export interface UpdateEUPartialDto {
  id: number;
  individualFarmerOrBreederGroupForProducts?: string;
  sizeProductionAreaToBeCertified?: number;
  numberOfParcels?: number;
  hasConversionProduction?: boolean;
  startDateOfOrganicProduction?: Date | null;
  hasNonorganicProduction?: boolean;
  sizeProductionArea?: number;
  numberOfAreasToBeProduced?: number;
  totalProductionArea?: number;
  numberOfManufacturers?: number;
  averageAreaPerProducer?: number;
  aeProducerOver5Ha?: boolean;
  numberOfProducerOver5Ha?: number;
  averageTravelTimeProducers?: number;
  numberOfDifferentRegionsProducers?: number;
  arePurchaseDistributionFromCentral?: boolean;
  areProduceConventionalProduct?: boolean;
  areParallelProduction?: boolean;
  hasInternalControlSystem?: boolean;
  totalTimeOfAnnualInternalAudit?: number;
  hasInternalQualitySystem?: boolean;
  totalAreaOfCollectionAreasToBeCertified?: number;
  annualEstimatedAmountCollected?: number;
  numberOfCollectionAreas?: number;
  numberOfCollectors?: number;
  numberOfOrganizers?: number;
  permissionLetterIds?: string[];
  hasAnyNonOrganicProduction?: boolean;
  estimatedAnnualAmountOfTradedProducts?: number;
  areThereARecertificationRequest?: boolean;
  totalNumberOfInputsOrganic?: number;
  totalNumberOfInputsNonOrganic?: number;
  weightType?: string;
  fileIds?: string[];
  nonOrganicProducts?: string;
  isThereStorageNonOrganic?: boolean;
}

export interface BaseStandardDocumentDto {
  id: number;
  fileId: string;
  file?: FileDto;
}

export interface GlobalGAPStandardDto extends BaseStandardDto {
  option1OrOption2?: string;
  haveYouEverBeenCertifiedWithinGlobalGap?: boolean;
  ggnNo?: number;
  registrationNumber?: number;
  certifiedByAnotherCertificationBody?: boolean;
  haveYouReceivedNonComplianceFromAnotherCertificationBody?: boolean;
  nameOfCertificationCompany?: string;
  sectionAndProductsCertifiedByOtherCertificationBody?: string;
  haveTheInconsistenciesBeenFixed?: boolean;
  isItWantedGrasp?: boolean;
  groupCertificationFileIds?: string[];
  groupCertificationFile?: FileDto;
  officialSiteRegistrationFiles?: BaseStandardDocumentDto[];
  groupCertificationFiles?: BaseStandardDocumentDto[];
  doYouRequestThatItBePublishedPublicly?: boolean;
  explanation?: string;
  permanentLocalWorkers?: number;
  permanentImmigrantWorkers?: number;
  permanentForeignerWorkers?: number;
  temporaryLocalWorkers?: number;
  temporaryImmigrantWorkers?: number;
  temporaryForeignerWorkers?: number;
  familyWorkLocalWorkers?: number;
  familyWorkImmigrantWorkers?: number;
  familyWorkForeignerWorkers?: number;
  hasSubcontractors?: boolean;
  whereIsTheProductProcessingDone?: string[];
  hasPPorPO?: boolean;
  doesProcessAnotherManufacturersProduct?: boolean;
  totalNumberOfProducer?: number;
  productProducerInformationFile?: BaseStandardDocumentDto;
}

export interface CreateGlobalGAPStandardDto extends CreateBaseStandardDto {
  option1OrOption2?: string;
  haveYouEverBeenCertifiedWithinGlobalGap?: boolean;
  ggnNo?: number;
  registrationNumber?: number;
  certifiedByAnotherCertificationBody?: boolean;
  haveYouReceivedNonComplianceFromAnotherCertificationBody?: boolean;
  nameOfCertificationCompany?: string;
  sectionAndProductsCertifiedByOtherCertificationBody?: string;
  haveTheInconsistenciesBeenFixed?: boolean;
  lastInspectionDate?: Date | null;
  isItWantedGrasp?: boolean;
  groupCertificationFileIds?: string[];
  officialSiteRegistrationFileIds?: string[];
  doYouRequestThatItBePublishedPublicly?: boolean;
  explanation?: string;
  permanentLocalWorkers?: number;
  permanentImmigrantWorkers?: number;
  permanentForeignerWorkers?: number;
  temporaryLocalWorkers?: number;
  temporaryImmigrantWorkers?: number;
  temporaryForeignerWorkers?: number;
  familyWorkLocalWorkers?: number;
  familyWorkImmigrantWorkers?: number;
  familyWorkForeignerWorkers?: number;
  totalNumberOfProducer?: number;
  productProducerInformationFileId?: string;
}

export interface UpdateGlobalGAPStandardDto {
  id: number;
  option1OrOption2?: string;
  haveYouEverBeenCertifiedWithinGlobalGap?: boolean;
  ggnNo?: number;
  registrationNumber?: number;
  certifiedByAnotherCertificationBody?: boolean;
  haveYouReceivedNonComplianceFromAnotherCertificationBody?: boolean;
  nameOfCertificationCompany?: string;
  sectionAndProductsCertifiedByOtherCertificationBody?: string;
  haveTheInconsistenciesBeenFixed?: boolean;
  lastInspectionDate?: Date | null;
  isItWantedGrasp?: boolean;
  groupCertificationFileIds?: string[];
  officialSiteRegistrationFileIds?: string[];
  doYouRequestThatItBePublishedPublicly?: boolean;
  explanation?: string;
  permanentLocalWorkers?: number;
  permanentImmigrantWorkers?: number;
  permanentForeignerWorkers?: number;
  temporaryLocalWorkers?: number;
  temporaryImmigrantWorkers?: number;
  temporaryForeignerWorkers?: number;
  familyWorkLocalWorkers?: number;
  familyWorkImmigrantWorkers?: number;
  familyWorkForeignerWorkers?: number;
  totalNumberOfProducer?: number;
  productProducerInformationFileId?: string;
}

export interface UpdateGlobalGAPPartialDto {
  id: number;
  hasSubcontractors?: boolean;
  whereIsTheProductProcessingDone?: string[];
  hasPPorPO?: boolean;
  doesProcessAnotherManufacturersProduct?: boolean;
  fileIds?: string[];
}

export interface GMOFreeStandardDto extends BaseStandardDto {
  certificationScopes?: string[];
  hasSocialCertification?: boolean;
  areComplySocialCriteria?: boolean;
  hasEnvironmentalPolicyAndWasteManagementPlan?: boolean;
  hasChildLabour?: boolean;
  hasPrecautionsBeenTakenForHealth?: boolean;
  hasProductionFlowChartAndStoragePlan?: boolean;
  hasProductSpecifiticationForEachProduct?: boolean;
}

export interface CreateGMOFreeStandardDto extends CreateBaseStandardDto {
  certificationScopes?: string[];
  hasSocialCertification?: boolean;
  areComplySocialCriteria?: boolean;
  hasEnvironmentalPolicyAndWasteManagementPlan?: boolean;
  hasChildLabour?: boolean;
  hasPrecautionsBeenTakenForHealth?: boolean;
  hasProductionFlowChartAndStoragePlan?: boolean;
  hasProductSpecifiticationForEachProduct?: boolean;
}

export interface UpdateGMOFreeStandardDto {
  id: number;
  certificationScopes?: string[];
  hasSocialCertification?: boolean;
  areComplySocialCriteria?: boolean;
  hasEnvironmentalPolicyAndWasteManagementPlan?: boolean;
  hasChildLabour?: boolean;
  hasPrecautionsBeenTakenForHealth?: boolean;
  hasProductionFlowChartAndStoragePlan?: boolean;
  hasProductSpecifiticationForEachProduct?: boolean;
  fileIds?: string[];
}

export interface UpdateGMOFreePartialDto {
  id: number;
  hasSubcontractors?: boolean;
  hasSubcontractorsAgreement?: boolean;
  areSubcontractorsCertified?: boolean;
  addionalComment?: string;
  hasInputProduction?: boolean;
  typeOfProductToBeApproved?: string;
  inputProductStandards?: string[];
  areGrowerGroup?: boolean;
  numberOfAreasToProduce?: number;
  totalProductionArea?: number;
  numberOfProducers?: number;
  averageAreaPerProducer?: number;
  averageTravelTimeProducers?: number;
  numberOfProducingRegions?: number;
  arePurchaseDistributionFromCentral?: boolean;
  areProduceConventionalProduct?: boolean;
  areParallelProduction?: boolean;
  hasInternalControlSystem?: boolean;
  TotalTimeOfAnnualInternalAudit?: number;
  hasInternalQualitySystem?: boolean;
  SizeProductionAreaToBeCertified?: number;
  NumberOfPlotsParcels?: number;
  ExpectedAnnualCropProductionQuantity?: number;
  hasInconversionProduction?: boolean;
  startDateOfOrganicProduction?: Date | null;
  files: string[];
  totalAreaToBeCertified?: number;
  annualEstimatedAmountCollected?: number;
  numberOfCollectionArea?: number;
  numberOfCollectors?: number;
  numberOfOrganizers?: number;
  numberOfBeehives?: number;
  areBeehivesLocatedMoreOneArea?: boolean;
  numberOfBeehivesDifferentArea?: number;
  estimatedAmountHoneyProduction?: number;
  hasAnyNonOrganicBeehives?: boolean;
  hasNonOrganicProductionForProcessing?: boolean;
  hasNonOrganicProductionForPreparation?: boolean;
  hasNonOrganicProductionForPostHarvest?: boolean;
  totalNumberOfInputsUsedAllProducts?: number;
  estimatedAnnualAmountOfTradedForImport?: number;
  estimatedAnnualAmountOfTradedForExport?: number;
  estimatedAnnualAmountOfTradedForTrade?: number;
  permanentLocalWorkers?: number;
  permanentImmigrantWorkers?: number;
  permanentForeignerWorkers?: number;
  temporaryLocalWorkers?: number;
  temporaryImmigrantWorkers?: number;
  temporaryForeignerWorkers?: number;
  familyWorkLocalWorkers?: number;
  familyWorkImmigrantWorkers?: number;
  familyWorkForeignerWorkers?: number;
}

export interface ITUStandardDto extends BaseStandardDto {
  herbalProductionType?: string[];
  certificationType?: string;
  numberOfGroupProducers?: number;
  hasProductionOrProductProcessingOutOfScope?: boolean;
  parallelProduction?: boolean;
  parallelOwnership?: boolean;
  isCounselingServiceReceived?: boolean;
  consultantCompanyName?: string;
  nameOfTechnicalAdvisors?: string;
  nameOfInternalAuditors?: string;
  isTheQualityManualReadyToAudit?: boolean;
  isTheQualityManagementSystemReadyToAudit?: boolean;
  companyGPSCodes?: string;
  areUpdatedSoilAnalyzesAvailable?: boolean;
  isTheHarvestingProcessDoneInHouse?: boolean;
  isTheProductJarvestedByTheCompanyPurchasingTheProduct?: boolean;
  explanation?: string;
  isTheHarvestDoneWithSeasonalWorkers?: boolean;
  areTheProductsDeliveredDirectlyFromTheFieldToTheWholesaler?: boolean;
  isTheProductPackagedInTheProductionArea?: boolean;
  isCertificationRequiredForProcessingAndPackaging?: boolean;
  isTheProcessingAndPackagingDoneOnTheFarm?: boolean;
  doesTheBusinessHaveIFSBRCSQF2000Certification?: boolean;
  doYouBuyAndPackCertifiedProductsOtherThanYourOwnProduction?: boolean;
  officialStructureDocuments?: BaseStandardDocumentDto[];
  whatAnalyzesAvailable?: string;
  productProducerInformationFile?: BaseStandardDocumentDto;
}

export interface CreateITUStandardDto extends CreateBaseStandardDto {
  herbalProductionType?: string[];
  certificationType?: string;
  numberOfGroupProducers?: number;
  hasProductionOrProductProcessingOutOfScope?: boolean;
  parallelProduction?: boolean;
  parallelOwnership?: boolean;
  isCounselingServiceReceived?: boolean;
  consultantCompanyName?: string;
  nameOfTechnicalAdvisors?: string;
  nameOfInternalAuditors?: string;
  isTheQualityManualReadyToAudit?: boolean;
  isTheQualityManagementSystemReadyToAudit?: boolean;
  companyGPSCodes?: string;
  areUpdatedSoilAnalyzesAvailable?: boolean;
  isTheHarvestingProcessDoneInHouse?: boolean;
  isTheProductJarvestedByTheCompanyPurchasingTheProduct?: boolean;
  explanation?: string;
  isTheHarvestDoneWithSeasonalWorkers?: boolean;
  areTheProductsDeliveredDirectlyFromTheFieldToTheWholesaler?: boolean;
  isTheProductPackagedInTheProductionArea?: boolean;
  isCertificationRequiredForProcessingAndPackaging?: boolean;
  isTheProcessingAndPackagingDoneOnTheFarm?: boolean;
  doesTheBusinessHaveIFSBRCSQF2000Certification?: boolean;
  doYouBuyAndPackCertifiedProductsOtherThanYourOwnProduction?: boolean;
  officialStructureDocumentIds: string[];
  whatAnalyzesAvailable?: string;
  productProducerInformationFileId?: string;
}

export interface UpdateITUStandardDto {
  id: number;
  herbalProductionType?: string[];
  certificationType?: string;
  numberOfGroupProducers?: number;
  hasProductionOrProductProcessingOutOfScope?: boolean;
  parallelProduction?: boolean;
  parallelOwnership?: boolean;
  isCounselingServiceReceived?: boolean;
  consultantCompanyName?: string;
  nameOfTechnicalAdvisors?: string;
  nameOfInternalAuditors?: string;
  isTheQualityManualReadyToAudit?: boolean;
  isTheQualityManagementSystemReadyToAudit?: boolean;
  companyGPSCodes?: string;
  areUpdatedSoilAnalyzesAvailable?: boolean;
  isTheHarvestingProcessDoneInHouse?: boolean;
  isTheProductJarvestedByTheCompanyPurchasingTheProduct?: boolean;
  explanation?: string;
  isTheHarvestDoneWithSeasonalWorkers?: boolean;
  areTheProductsDeliveredDirectlyFromTheFieldToTheWholesaler?: boolean;
  isTheProductPackagedInTheProductionArea?: boolean;
  isCertificationRequiredForProcessingAndPackaging?: boolean;
  isTheProcessingAndPackagingDoneOnTheFarm?: boolean;
  doesTheBusinessHaveIFSBRCSQF2000Certification?: boolean;
  doYouBuyAndPackCertifiedProductsOtherThanYourOwnProduction?: boolean;
  officialStructureDocumentIds?: string[];
  fileIds?: string[];
  whatAnalyzesAvailable?: string;
  productProducerInformationFileId?: string;
}

export interface OfficialStructureDocument {
  baseStandardId: number;
  baseStandard: BaseStandardDto;
  fileId: string;
  file: File;
}

export interface HistoricalDocumentDto {
  offerFiles: FileDto[];
  contractFiles: FileDto[];
  proformaFiles: FileDto[];
  applicationPDFFiles: FileDto[];
  invoiceFiles: FileDto[];
  receiptFiles: FileDto[];
  rejectionNoticeFiles: FileDto[];
}

export interface NewDto {
  type: string;
  files: FileDto[];
}
